<template>
  <MapsMultiple
    :features="features || []"
    no-close
    :no-route-update="noRouteUpdate"
  />
</template>

<script lang="ts" setup>
import type {
  ParagraphTeaserFragment,
  TeaserMapNodeFragment,
} from '#graphql-operations'
import type { MapPoiFeature } from '~/helpers/maps'

const props = withDefaults(
  defineProps<{
    nids?: string[]
    externalBlocks?: ParagraphTeaserFragment[]
    noRouteUpdate?: boolean
  }>(),
  {
    nids: () => [],
    externalBlocks: () => [],
    noRouteUpdate: false,
  },
)

const currentLanguage = useCurrentLanguage()

function mapExternalBlock(v: ParagraphTeaserFragment): MapPoiFeature | null {
  if (
    v.address &&
    'location' in v.address &&
    v.address.location?.lat &&
    v.address.location.lon &&
    v.image &&
    v.link?.uri?.path
  ) {
    return {
      id: v.uuid,
      title: v.title || '',
      lat: v.address.location?.lat,
      lon: v.address.location?.lon,
      media: v.image,
      url: v.link?.uri.path,
    }
  }
  return null
}

function mapNode(v: TeaserMapNodeFragment): MapPoiFeature | null {
  const id = v.nid
  if (!id) {
    return null
  }
  const title = v.title || ''
  const address = v.address
  if (!address) {
    return null
  }
  const lat = address.location?.lat
  const lon = address.location?.lon
  if (!lat || !lon) {
    return null
  }
  return {
    id,
    title,
    lat,
    lon,
    media: v.teaserImage,
    url: v.url?.path || '',
    address,
  }
}

const { data: features } = await useAsyncGraphqlQuery(
  'teaserMap',
  {
    nids: props.nids.map((nid) => nid.toString()),
    language: currentLanguage.value,
  },
  {
    transform(data) {
      const nodes = data.data.entityQuery?.entities || []
      return [
        ...nodes.map((v) => {
          if (!v) {
            return null
          }

          if (!('nid' in v)) {
            return null
          }

          return mapNode(v)
        }),
        ...props.externalBlocks.map(mapExternalBlock),
      ].filter((v): v is MapPoiFeature => v !== null)
    },
  },
)
</script>
